import { Box, Card, CardContent, CircularProgress, Collapse, Divider, IconButton, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CircleIcon from '@mui/icons-material/Circle';
import RemoveIcon from '@mui/icons-material/Remove';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import strings from 'common/Translation/Translate';
import BookingStatusChip from 'components/BookingStatusChip/BookingStatusChip';
import { useMemo, useState } from 'react';
import type { PricingInsightsData } from './PricingInsights';

interface AllocatedDiscount {
  description?: string;
  amount?: number;
  formattedAmount?: string;
}

interface LineItem {
  header?: boolean;
  text: string;
  amount?: string | number;
  unformattedAmount?: number;
  grossAmount?: number;
  formattedGrossAmount?: string;
  itemDiscount?: number;
  formattedItemDiscount?: string;
  serviceCharge?: number;
  formattedServiceCharge?: string;
  /** True when SERVICE_CHARGE_TAX_CODES is configured — SC is taxed separately so the
   *  taxable amount is the gross only, which would be confusing to show. */
  serviceChargeExcludedFromTaxable?: boolean;
  allocatedOverallDiscount?: number;
  formattedAllocatedOverallDiscount?: string;
  allocatedDiscounts?: AllocatedDiscount[];
  taxableAmount?: number;
  formattedTaxableAmount?: string;
  displayAmount?: number;
  itemTotal?: number;
  formattedItemTotal?: string;
  taxBreakup?: LineItem[];
  venueName?: string;
  dateLabel?: string;
  eventSubtype?: string;
  timeLabel?: string;
  sessionLabel?: string;
  status?: string;
  statusLabel?: string;
  eventSortKey?: number;
}

interface CostingPageResponse {
  lineItems: LineItem[];
  taxTotals?: LineItem[];
  total: string | number;
  totalUnformatted?: number;
  pricingInsights?: PricingInsightsData | null;
}

export type { CostingPageResponse, LineItem as CostingLineItem };

interface CostingComponentProps {
  pageResponse: CostingPageResponse;
  /** Card heading; defaults to the new-booking copy. */
  title?: string;
  /** Shown below the grand total (e.g. booking detail billing). */
  perGuestInsight?: { label: string; formattedAmount: string };
  /** Overrides the default total row label (e.g. booking detail). */
  grandTotalLabel?: string;
  /** Larger, stronger typography for the total row (booking detail billing). */
  grandTotalEmphasized?: boolean;
  /** Adds a Download Invoice action next to the title. */
  downloadAction?: {
    onClick: () => void;
    label?: string;
    loading?: boolean;
    disabled?: boolean;
  };
  reviewLayout?: boolean;
  /** Tighter card spacing for venue-details sidebar. */
  compact?: boolean;
  /** Status chip next to venue header — new booking only. */
  showStatusChip?: boolean;
}

const SKIPPED_REVIEW_HEADERS = new Set(['Charges', '']);

type ReviewChargeGroup = {
  header?: LineItem;
  sectionHeader?: string;
  items: LineItem[];
  subtotal: number;
};

const hasEventGroupHeader = (item: LineItem) => !!item.header && !!item.venueName?.trim();

const sortReviewChargeGroups = (groups: ReviewChargeGroup[]) =>
  [...groups].sort((a, b) => {
    const aKey = a.header?.eventSortKey ?? Number.MAX_SAFE_INTEGER;
    const bKey = b.header?.eventSortKey ?? Number.MAX_SAFE_INTEGER;
    return aKey - bKey;
  });

const buildReviewChargeGroups = (items: LineItem[]): ReviewChargeGroup[] => {
  const result: ReviewChargeGroup[] = [];
  let current: ReviewChargeGroup | null = null;

  const flush = () => {
    if (current && (current.items.length > 0 || current.header || current.sectionHeader)) {
      result.push(current);
    }
    current = null;
  };

  for (const item of items) {
    if (item.header) {
      const label = item.text?.trim() ?? '';
      if (SKIPPED_REVIEW_HEADERS.has(label)) {
        continue;
      }
      flush();
      if (hasEventGroupHeader(item)) {
        current = { header: item, items: [], subtotal: 0 };
      } else {
        current = { sectionHeader: label, items: [], subtotal: 0 };
      }
      continue;
    }

    if (!current) {
      current = { items: [], subtotal: 0 };
    }
    current.items.push(item);
    if (item.itemTotal != null && !Number.isNaN(Number(item.itemTotal))) {
      current.subtotal += Number(item.itemTotal);
    }
  }

  flush();
  return result;
};

const amountSx = {
  textAlign: 'right' as const,
  fontVariantNumeric: 'tabular-nums' as const,
  justifySelf: 'end' as const,
  minWidth: '6.25rem',
};

const formatMoney = (value?: number) => {
  if (value == null || Number.isNaN(Number(value))) {
    return '—';
  }
  return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDeduction = (value?: number) => {
  if (value == null || Number(value) <= 0) {
    return null;
  }
  return `-${formatMoney(value)}`;
};

/** Prefer API {@link CommonUtilService#formatCurrency} strings; fall back to local formatting. */
const displayAmount = (formatted?: string, raw?: number) =>
  formatted ?? (raw != null && !Number.isNaN(Number(raw)) ? formatMoney(raw) : '—');

const displayDeduction = (formatted?: string, raw?: number) => {
  if (formatted) {
    return `-${formatted}`;
  }
  return formatDeduction(raw);
};

const hasDetailBreakdown = (item: LineItem) =>
  item.grossAmount != null ||
  item.serviceCharge != null ||
  item.itemDiscount != null ||
  item.allocatedOverallDiscount != null ||
  !!item.allocatedDiscounts?.length ||
  item.taxableAmount != null ||
  !!item.taxBreakup?.length ||
  item.itemTotal != null;

const renderBreakdownDivider = (theme: Theme, key: string) => (
  <Divider
    key={key}
    sx={{
      my: 0.375,
      borderStyle: 'dashed',
      borderColor: alpha(theme.palette.divider, 0.65),
    }}
  />
);

const collapsedLineAmount = (item: LineItem, reviewStyle: boolean) => {
  if (reviewStyle && (item.formattedGrossAmount != null || item.grossAmount != null)) {
    return displayAmount(item.formattedGrossAmount, item.grossAmount);
  }
  return item.amount;
};

const SERVICE_CHARGE_SUMMARY_LABELS = new Set([
  'Total Service Charge',
  'Total Service Charges',
  'Service Charge',
]);

const isServiceChargeSummaryLine = (item: LineItem) =>
  !item.header && SERVICE_CHARGE_SUMMARY_LABELS.has(item.text?.trim() ?? '');

const NEGATIVE_FOOTER_LABELS = new Set(['Discount', 'Round-off']);

const lineAmountIsNegative = (item: LineItem) => {
  if (typeof item.unformattedAmount === 'number') return item.unformattedAmount < 0;
  if (typeof item.amount === 'number') return item.amount < 0;
  if (typeof item.amount === 'string') return item.amount.trim().startsWith('-');
  return false;
};

const isNegativeFooterLine = (item: LineItem) =>
  NEGATIVE_FOOTER_LABELS.has(item.text?.trim() ?? '') && lineAmountIsNegative(item);

export const CostingComponent = ({
  pageResponse,
  title = 'Booking Cost Breakdown',
  perGuestInsight,
  grandTotalLabel,
  grandTotalEmphasized = false,
  downloadAction,
  reviewLayout = false,
  compact = false,
  showStatusChip = false,
}: CostingComponentProps) => {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const chargeLineItems =
    pageResponse?.lineItems?.filter(item => !isServiceChargeSummaryLine(item)) ?? [];
  const useReviewLayout =
    reviewLayout || chargeLineItems.some(item => hasEventGroupHeader(item));
  const reviewChargeGroups = useMemo(() => {
    if (!useReviewLayout) return [];
    return sortReviewChargeGroups(buildReviewChargeGroups(chargeLineItems));
  }, [useReviewLayout, chargeLineItems]);
  const legacyServiceChargeLine = pageResponse?.lineItems?.find(isServiceChargeSummaryLine);
  const taxTotalsFromApi = pageResponse?.taxTotals ?? [];
  const hasServiceChargeInTaxTotals = taxTotalsFromApi.some(item => isServiceChargeSummaryLine(item));
  const taxesAndServiceCharges = [
    ...(legacyServiceChargeLine && !hasServiceChargeInTaxTotals
      ? [{ ...legacyServiceChargeLine, text: strings.bookingDetails_billing_serviceCharge }]
      : []),
    ...taxTotalsFromApi,
  ];
  const showTaxesAndServiceCharges = taxesAndServiceCharges.length > 0;

  const bodyFont = compact ? '0.7rem' : '0.8125rem';
  const captionFont = compact ? '0.65rem' : undefined;
  const titleFont = compact ? '0.75rem' : undefined;
  const totalFont = compact ? '0.8125rem' : '0.9375rem';
  const amountCol = compact ? 'minmax(5.25rem, max-content)' : 'minmax(6.25rem, max-content)';

  const toggleRow = (rowKey: string) => {
    setExpandedRows(prev => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  const lineGrid = compact
    ? `22px minmax(0, 1fr) ${amountCol}`
    : `28px minmax(0, 1fr) ${amountCol}`;

  const renderBreakdownRow = (
    key: string,
    label: string,
    value: string,
    options?: { emphasis?: boolean; deduction?: boolean; dividerAbove?: boolean },
  ) => (
    <Box
      key={key}
      sx={{
        display: 'grid',
        gridTemplateColumns: `minmax(0, 1fr) ${amountCol}`,
        columnGap: 1,
        alignItems: 'baseline',
        py: 0.125,
        ...(options?.dividerAbove
          ? {
              mt: 0.25,
              pt: 0.375,
              borderTop: `1px dashed ${alpha(theme.palette.divider, 0.65)}`,
            }
          : {}),
      }}>
      <Typography
        variant="caption"
        color={options?.deduction ? 'error.main' : options?.emphasis ? 'text.primary' : 'text.secondary'}
        fontWeight={options?.emphasis ? 600 : 400}
        sx={{ lineHeight: 1.35, fontSize: captionFont }}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        fontWeight={options?.emphasis || options?.deduction ? 600 : 500}
        color={options?.deduction ? 'error.main' : 'text.primary'}
        sx={{ ...amountSx, lineHeight: 1.35, fontSize: captionFont }}>
        {value}
      </Typography>
    </Box>
  );

  const renderAllocatedDiscountRows = (item: LineItem, rowKey: string) => {
    const named = item.allocatedDiscounts?.filter(
      slice => slice.amount != null && Number(slice.amount) > 0,
    );
    if (named && named.length > 0) {
      return named.map((slice, idx) =>
        renderBreakdownRow(
          `${rowKey}-overall-discount-${idx}`,
          slice.description?.trim() || 'Discount',
          displayDeduction(slice.formattedAmount, slice.amount) ?? '',
          { deduction: true },
        ),
      );
    }
    const overallDiscountLabel = displayDeduction(
      item.formattedAllocatedOverallDiscount,
      item.allocatedOverallDiscount,
    );
    if (!overallDiscountLabel) return null;
    return renderBreakdownRow(
      `${rowKey}-overall-discount`,
      'Allocated overall discount',
      overallDiscountLabel,
      { deduction: true },
    );
  };

  const renderReviewExpandedBreakdown = (item: LineItem, rowKey: string) => {
    const itemDiscountLabel = displayDeduction(item.formattedItemDiscount, item.itemDiscount);
    const serviceChargeLabel =
      item.formattedServiceCharge ??
      (item.serviceCharge != null && item.serviceCharge > 0
        ? displayAmount(undefined, item.serviceCharge)
        : null);
    const hasTaxSection =
      item.taxableAmount != null || (item.taxBreakup != null && item.taxBreakup.length > 0);

    return (
      <>
        {(item.formattedGrossAmount != null || item.grossAmount != null) &&
          renderBreakdownRow(
            `${rowKey}-gross`,
            'Gross amount',
            displayAmount(item.formattedGrossAmount, item.grossAmount),
          )}
        {itemDiscountLabel &&
          renderBreakdownRow(`${rowKey}-item-discount`, 'Item discount', itemDiscountLabel, {
            deduction: true,
          })}
        {renderAllocatedDiscountRows(item, rowKey)}
        {serviceChargeLabel != null &&
          renderBreakdownRow(
            `${rowKey}-service-charge`,
            strings.bookingDetails_billing_serviceCharge,
            serviceChargeLabel,
          )}
        {hasTaxSection && renderBreakdownDivider(theme, `${rowKey}-divider-tax`)}
        {(item.formattedTaxableAmount != null || item.taxableAmount != null) &&
          renderBreakdownRow(
            `${rowKey}-taxable`,
            'Taxable value',
            displayAmount(item.formattedTaxableAmount, item.taxableAmount),
            { emphasis: true },
          )}
        {item.taxBreakup?.map((taxItem, taxIdx) =>
          renderBreakdownRow(`${rowKey}-tax-${taxIdx}`, taxItem.text, String(taxItem.amount)),
        )}
        {(item.formattedItemTotal != null || item.itemTotal != null) && (
          <>
            {renderBreakdownDivider(theme, `${rowKey}-divider-total`)}
            {renderBreakdownRow(
              `${rowKey}-item-total`,
              'Item total',
              displayAmount(item.formattedItemTotal, item.itemTotal),
              { emphasis: true },
            )}
          </>
        )}
      </>
    );
  };

  const renderDetailLine = (item: LineItem, rowKey: string, reviewStyle = false) => {
    const hasBreakup = hasDetailBreakdown(item);
    const isExpanded = !!expandedRows[rowKey];
    const itemDiscountLabel = displayDeduction(item.formattedItemDiscount, item.itemDiscount);
    const negativeFooter = isNegativeFooterLine(item);

    return (
      <Box key={rowKey} sx={{ py: 0.125 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: lineGrid,
            columnGap: 0.5,
            alignItems: 'center',
          }}>
          <Box display="flex" alignItems="center" justifyContent="center" sx={{ width: compact ? 22 : 28 }}>
            {hasBreakup ? (
              <IconButton
                size="small"
                onClick={() => toggleRow(rowKey)}
                sx={{
                  width: compact ? 18 : 22,
                  height: compact ? 18 : 22,
                  p: 0,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                  color: 'primary.main',
                }}
                aria-label={isExpanded ? 'Hide line breakup' : 'Show line breakup'}>
                {isExpanded ? (
                  <RemoveIcon sx={{ fontSize: compact ? 12 : 14 }} />
                ) : (
                  <AddIcon sx={{ fontSize: compact ? 12 : 14 }} />
                )}
              </IconButton>
            ) : (
              <CircleIcon
                sx={{
                  fontSize: compact ? 4 : 5,
                  color: alpha(theme.palette.primary.main, 0.35),
                }}
              />
            )}
          </Box>
          <Typography
            variant="body2"
            color={negativeFooter ? 'error.main' : 'text.secondary'}
            sx={{ fontSize: bodyFont, lineHeight: 1.3 }}>
            {item.text}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={negativeFooter ? 600 : 500}
            color={negativeFooter ? 'error.main' : 'text.primary'}
            sx={{ ...amountSx, fontSize: bodyFont }}>
            {collapsedLineAmount(item, reviewStyle)}
          </Typography>
        </Box>

        {hasBreakup && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                mt: 0.25,
                ml: compact ? 2.75 : 3.5,
                pl: compact ? 0.75 : 1,
                borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.14)}`,
              }}>
              {reviewStyle
                ? renderReviewExpandedBreakdown(item, rowKey)
                : (
                  <>
                    {item.grossAmount != null &&
                      renderBreakdownRow(
                        `${rowKey}-gross`,
                        'Gross amount',
                        displayAmount(item.formattedGrossAmount, item.grossAmount),
                      )}
                    {itemDiscountLabel &&
                      renderBreakdownRow(`${rowKey}-item-discount`, 'Item discount', itemDiscountLabel, {
                        deduction: true,
                      })}
                    {renderAllocatedDiscountRows(item, rowKey)}
                    {item.taxableAmount != null && !item.serviceChargeExcludedFromTaxable &&
                      renderBreakdownRow(
                        `${rowKey}-taxable`,
                        'Taxable value',
                        displayAmount(item.formattedTaxableAmount, item.taxableAmount),
                        { emphasis: true, dividerAbove: true },
                      )}
                    {item.taxBreakup?.map((taxItem, taxIdx) =>
                      renderBreakdownRow(
                        `${rowKey}-tax-${taxIdx}`,
                        taxItem.text,
                        String(taxItem.amount),
                        taxIdx === 0 && item.serviceChargeExcludedFromTaxable ? { dividerAbove: true } : undefined,
                      ),
                    )}
                    {item.itemTotal != null &&
                      renderBreakdownRow(
                        `${rowKey}-item-total`,
                        'Item total',
                        displayAmount(item.formattedItemTotal, item.itemTotal),
                        { emphasis: true, dividerAbove: true },
                      )}
                  </>
                )}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  const renderLegacyHeader = (item: LineItem, idx: number) => {
    const showHeaderDivider = idx !== 0 && item.header;
    return (
      <Box key={`header-${idx}`} sx={{ mt: idx > 0 ? 0.75 : 0 }}>
        {showHeaderDivider && (
          <Divider
            sx={{
              my: 0.5,
              borderStyle: 'dashed',
              borderColor: alpha(theme.palette.divider, 0.9),
            }}
          />
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `minmax(0, 1fr) ${amountCol}`,
            columnGap: 1,
            alignItems: 'baseline',
            py: 0.125,
          }}>
          <Typography variant="caption" fontWeight={600} sx={{ color: 'text.primary', fontSize: captionFont }}>
            {item.text}
          </Typography>
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            sx={{ ...amountSx, fontSize: captionFont }}>
            {item.amount !== '' && item.amount != null ? item.amount : '\u00a0'}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderReviewGroups = () =>
    reviewChargeGroups.map((group, groupIdx) => {
      const header = group.header;
      const subtitleParts = [header?.eventSubtype, header?.timeLabel, header?.sessionLabel].filter(Boolean);

      return (
        <Box key={`review-group-${groupIdx}`}>
          {groupIdx > 0 && (
            <Divider
              sx={{
                my: compact ? 0.75 : 1,
                borderStyle: 'dashed',
                borderColor: alpha(theme.palette.divider, 0.75),
              }}
            />
          )}
          {header && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: compact ? 1 : 1.5,
                mb: compact ? 0.5 : 0.75,
              }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ lineHeight: 1.3, fontSize: compact ? '0.75rem' : undefined }}>
                  {header.venueName}
                </Typography>
                {header.dateLabel ? (
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      lineHeight: 1.35,
                      mt: 0.15,
                      fontSize: compact ? '0.7rem' : bodyFont,
                      whiteSpace: 'pre-line',
                    }}>
                    {header.dateLabel}
                  </Typography>
                ) : null}
                {subtitleParts.length > 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.3, mt: 0.15, fontSize: bodyFont }}>
                    {subtitleParts.join(' - ')}
                  </Typography>
                )}
              </Box>
              {showStatusChip ? (
                <BookingStatusChip status={header.status} label={header.statusLabel} />
              ) : null}
            </Box>
          )}
          {group.sectionHeader && (
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ color: 'text.primary', display: 'block', mb: 0.5, fontSize: captionFont }}>
              {group.sectionHeader}
            </Typography>
          )}
          {group.items.map((item, itemIdx) => renderDetailLine(item, `${groupIdx}-${itemIdx}`, true))}
        </Box>
      );
    });

  return (
    <Card
      sx={{
        width: '100%',
        my: compact ? 0 : 1,
        borderRadius: compact ? 1.25 : 1.5,
        boxShadow: compact ? 'none' : '0 1px 4px rgba(0,0,0,0.07)',
        border: `1px dashed ${alpha(theme.palette.divider, compact ? 0.45 : 0.55)}`,
      }}>
      <CardContent sx={{ px: compact ? 1 : 2, py: compact ? 0.85 : 1.5, '&:last-child': { pb: compact ? 0.85 : 1.5 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: compact ? 0.5 : 1,
          }}>
          <Typography
            variant={compact ? 'body2' : 'subtitle1'}
            fontWeight={700}
            sx={{ color: 'text.primary', fontSize: titleFont }}>
            {title}
          </Typography>
          {downloadAction && (
            <Tooltip title={downloadAction.label ?? 'Download Invoice'} arrow>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={downloadAction.onClick}
                  disabled={downloadAction.disabled || downloadAction.loading}
                  aria-label={downloadAction.label ?? 'Download Invoice'}
                  sx={{
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                    borderRadius: 1,
                    width: 28,
                    height: 28,
                    p: 0,
                  }}>
                  {downloadAction.loading ? (
                    <CircularProgress size={14} thickness={5} />
                  ) : (
                    <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>

        {useReviewLayout && reviewChargeGroups.length > 0
          ? renderReviewGroups()
          : chargeLineItems.map((item, idx) =>
              item.header ? renderLegacyHeader(item, idx) : renderDetailLine(item, String(idx)),
            )}

        {showTaxesAndServiceCharges && (
          <>
            <Divider
              sx={{
                my: 0.75,
                borderStyle: 'dashed',
                borderColor: alpha(theme.palette.info.main, 0.22),
              }}
            />

            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ mb: 0.5, display: 'block', color: 'text.primary', fontSize: captionFont }}>
              {strings.bookingDetails_text_taxesAndServiceCharges}
            </Typography>

            <Box
              sx={{
                px: compact ? 0.75 : 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.info.main, 0.04),
              }}>
              {taxesAndServiceCharges.map((taxItem, taxIdx) => (
                <Box
                  key={`tax-total-${taxIdx}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `minmax(0, 1fr) ${amountCol}`,
                    columnGap: 1,
                    alignItems: 'baseline',
                    py: 0.125,
                  }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: bodyFont }}>
                    {taxItem.text}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="text.primary"
                    sx={{ ...amountSx, fontSize: bodyFont }}>
                    {taxItem.amount}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}

        <Divider
          sx={{
            my: compact ? 0.5 : 0.75,
            borderStyle: 'dashed',
            borderColor: alpha(theme.palette.primary.main, 0.25),
          }}
        />

        <Box
          display="grid"
          gridTemplateColumns={`minmax(0, 1fr) ${amountCol}`}
          alignItems="baseline"
          columnGap={1}
          sx={{
            py: grandTotalEmphasized ? 1.125 : compact ? 0.5 : 0.75,
            px: compact ? 0.85 : 1.25,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 1,
          }}>
          <Typography
            variant={grandTotalEmphasized ? 'subtitle1' : 'body2'}
            fontWeight={800}
            color="primary.main"
            sx={
              grandTotalEmphasized
                ? { fontSize: '1rem', letterSpacing: 0.04, lineHeight: 1.3 }
                : { fontSize: totalFont }
            }>
            {grandTotalLabel ??
              (useReviewLayout
                ? strings.bookingDetails_billing_totalAmountLine
                : strings.totalText)}
          </Typography>
          <Typography
            variant={grandTotalEmphasized ? 'subtitle1' : 'body2'}
            fontWeight={800}
            color="primary.main"
            sx={{
              ...amountSx,
              fontSize: grandTotalEmphasized ? '1.25rem' : totalFont,
              lineHeight: grandTotalEmphasized ? 1.25 : undefined,
            }}>
            {pageResponse.total}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
